
import React, { useState, useEffect } from 'react';
import { dynamicPricingService } from '../services/dynamicPricingService';
import { storage } from '../lib/storage';
import { Product, DynamicPricingConfig } from '../types';
import { 
  BrainCircuit, 
  Zap, 
  TrendingUp, 
  Settings2, 
  AlertCircle, 
  Loader2, 
  Check, 
  Sparkles, 
  BarChart3, 
  LineChart,
  ShieldCheck,
  RefreshCw,
  ArrowRight,
  Info
} from 'lucide-react';

interface Props {
  storeId: string;
}

const AdminDynamicPricing: React.FC<Props> = ({ storeId }) => {
  const [config, setConfig] = useState<DynamicPricingConfig>(dynamicPricingService.getStoreConfig(storeId));
  const [products, setProducts] = useState<Product[]>([]);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = () => {
    const storeProducts = storage.getProducts().filter(p => p.storeId === storeId);
    setProducts(storeProducts);
  };

  const handleSaveConfig = () => {
    setIsSaving(true);
    dynamicPricingService.saveConfig(storeId, config);
    setTimeout(() => {
      setIsSaving(false);
      alert("AI Pricing rules updated successfully!");
    }, 1000);
  };

  const triggerAiSync = async () => {
    setIsSyncing(true);
    const recs = await dynamicPricingService.generatePriceRecommendations(storeId, products);
    if (recs) {
       setRecommendations(recs);
       loadData(); // To show updated adjustment badges
    }
    setIsSyncing(false);
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">AI Price Engine</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Dynamic Revenue Optimization</p>
        </div>
        <div className="flex gap-4">
           <button 
             onClick={triggerAiSync}
             disabled={isSyncing || !config.enabled}
             className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2 disabled:opacity-50"
           >
             {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
             Sync AI Prices
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-8">
            {/* Rules Configuration */}
            <section className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-10">
               <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl"><Settings2 className="h-6 w-6" /></div>
                  <h3 className="text-xl font-black text-gray-900">Optimization Rules</h3>
               </div>

               <div className="p-8 bg-blue-50 rounded-3xl border border-blue-100 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-blue-600 shadow-sm"><BrainCircuit className="h-6 w-6" /></div>
                    <div>
                      <h4 className="font-black text-blue-900">Enable AI Engine</h4>
                      <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest">Automatic Price Adjustments</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setConfig({ ...config, enabled: !config.enabled })}
                    className={`w-14 h-8 rounded-full transition-all relative p-1 ${config.enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                  >
                     <div className={`w-6 h-6 bg-white rounded-full transition-all ${config.enabled ? 'translate-x-6' : 'translate-x-0 shadow-md'}`}></div>
                  </button>
               </div>

               <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 transition-opacity ${!config.enabled ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
                  <div className="space-y-4">
                     <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Max Allowed Discount (%)</label>
                     <input 
                        type="number" 
                        value={config.maxDiscountPercentage}
                        onChange={e => setConfig({...config, maxDiscountPercentage: Number(e.target.value)})}
                        className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-black text-xl focus:ring-2 focus:ring-blue-600"
                     />
                     <p className="text-[9px] text-gray-400 font-bold">AI will never discount more than this.</p>
                  </div>
                  <div className="space-y-4">
                     <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Risk Strategy</label>
                     <div className="grid grid-cols-1 gap-2">
                        {['AGGRESSIVE', 'BALANCED', 'CONSERVATIVE'].map(s => (
                          <button 
                            key={s}
                            onClick={() => setConfig({...config, strategy: s as any})}
                            className={`p-4 rounded-xl border-2 text-[10px] font-black uppercase tracking-widest transition-all ${config.strategy === s ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-50 bg-gray-50 text-gray-400'}`}
                          >
                             {s}
                          </button>
                        ))}
                     </div>
                  </div>
               </div>

               <div className={`pt-6 border-t border-gray-50 flex items-center justify-between ${!config.enabled ? 'opacity-30' : ''}`}>
                  <div className="space-y-1">
                     <h4 className="font-black text-gray-900 text-sm">Auto-Apply Mode</h4>
                     <p className="text-xs text-gray-400 font-medium">Apply AI changes instantly without manual approval.</p>
                  </div>
                  <button 
                    onClick={() => setConfig({ ...config, autoApply: !config.autoApply })}
                    className={`w-12 h-6 rounded-full transition-all relative p-1 ${config.autoApply ? 'bg-emerald-500' : 'bg-gray-200'}`}
                  >
                     <div className={`w-4 h-4 bg-white rounded-full transition-all ${config.autoApply ? 'translate-x-6' : 'translate-x-0'}`}></div>
                  </button>
               </div>

               <button 
                 onClick={handleSaveConfig}
                 disabled={isSaving}
                 className="w-full py-5 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-black transition-all flex items-center justify-center gap-2"
               >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sync Engine Rules <ArrowRight className="h-4 w-4" /></>}
               </button>
            </section>

            {/* Recommendations Log */}
            {recommendations.length > 0 && (
               <section className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden animate-in slide-in-from-top-4">
                  <div className="p-8 border-b border-gray-50 flex items-center gap-3">
                     <Sparkles className="h-6 w-6 text-amber-500" />
                     <h3 className="text-xl font-black text-gray-900">Latest AI Adjustments</h3>
                  </div>
                  <div className="divide-y divide-gray-50">
                     {recommendations.map((rec, idx) => {
                       const p = products.find(prod => prod.id === rec.productId);
                       return (
                         <div key={idx} className="p-8 flex items-center justify-between hover:bg-gray-50 transition-all">
                            <div className="flex items-center gap-6">
                               <div className="w-12 h-12 bg-gray-100 rounded-xl overflow-hidden shrink-0">
                                  <img src={p?.images[0]} className="w-full h-full object-cover" alt="" />
                               </div>
                               <div>
                                  <p className="font-black text-gray-900">{p?.name}</p>
                                  <p className="text-xs text-gray-400 font-medium italic">"{rec.reason}"</p>
                               </div>
                            </div>
                            <div className="text-right">
                               <p className="text-xl font-black text-emerald-600">-{rec.recommendedDiscount}%</p>
                               <p className="text-[9px] font-black text-gray-400 uppercase">Discount Shift</p>
                            </div>
                         </div>
                       );
                     })}
                  </div>
               </section>
            )}
         </div>

         <div className="space-y-8">
            <div className="bg-blue-600 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform"><BarChart3 className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">Revenue Intel</h3>
                  <p className="text-blue-100 text-sm leading-relaxed">
                     Bhai, Dynamic Pricing enable karne ke baad average revenue per product typically <span className="text-white font-black underline">18% se grow</span> karta hai.
                  </p>
                  <div className="p-6 bg-white/5 rounded-3xl border border-white/10 space-y-4">
                     <div className="flex items-center gap-3">
                        <Zap className="h-5 w-5 text-amber-400" />
                        <p className="text-xs font-bold uppercase tracking-widest">Inventory Liquidation</p>
                     </div>
                     <div className="flex items-center gap-3">
                        <LineChart className="h-5 w-5 text-emerald-400" />
                        <p className="text-xs font-bold uppercase tracking-widest">Demand Optimization</p>
                     </div>
                  </div>
               </div>
            </div>

            <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-6">
               <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Product Health View</h4>
               <div className="space-y-4">
                  {products.slice(0, 3).map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                       <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gray-100 overflow-hidden"><img src={p.images[0]} className="w-full h-full object-cover" alt="" /></div>
                          <p className="text-xs font-bold text-gray-700 truncate w-24">{p.name}</p>
                       </div>
                       <span className={`text-[8px] font-black px-2 py-1 rounded-full uppercase ${p.dynamicPriceAdjustment ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-50 text-gray-400'}`}>
                          {p.dynamicPriceAdjustment ? `AI: -${p.dynamicPriceAdjustment}%` : 'Standard'}
                       </span>
                    </div>
                  ))}
               </div>
            </div>

            <div className="bg-gray-900 p-8 rounded-[2.5rem] border border-gray-800 flex items-start gap-4 text-white">
               <ShieldCheck className="h-6 w-6 text-emerald-400 shrink-0 mt-1" />
               <p className="text-[10px] text-gray-400 font-medium leading-relaxed uppercase tracking-wider">
                  Compliance Check: Dynamic Pricing follows all major consumer protection standards for fair pricing.
               </p>
            </div>
         </div>
      </div>
    </div>
  );
};

export default AdminDynamicPricing;
